<script setup lang="ts">
  import { computed } from 'vue';
  import { IUser } from '@/interfaces/IUser';

  const props = defineProps<{
    user: IUser;
    restaurantCount: number;
    activeRestaurants: number;
  }>();

  // Get time-based greeting
  const greeting = computed(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  });

  // Get motivational message based on restaurant count
  const motivationalMessage = computed(() => {
    if (props.restaurantCount === 0) {
      return '¡Comience su viaje digital creando su primera tienda!';
    } else if (props.restaurantCount === 1) {
      return 'Su tienda digital está lista para recibir pedidos.';
    } else {
      return `Administrando ${props.restaurantCount} tiendas exitosamente.`;
    }
  });
</script>

<template>
  <div class="welcome-hero">
    <div class="hero-content">
      <div class="greeting-section">
        <h1 class="greeting">{{ greeting }}, {{ user.name || user.email }}</h1>
        <p class="subtitle">
          {{ motivationalMessage }}
        </p>
      </div>

      <div class="hero-stats">
        <div class="stat-item">
          <v-icon class="stat-icon">mdi-store</v-icon>
          <div class="stat-content">
            <span class="stat-value">{{ restaurantCount }}</span>
            <span class="stat-label">Tiendas</span>
          </div>
        </div>
        <!-- <div class="stat-item">
          <v-icon class="stat-icon">mdi-check-circle</v-icon>
          <div class="stat-content">
            <span class="stat-value">{{ activeRestaurants }}</span>
            <span class="stat-label">Activas</span>
          </div>
        </div> -->
      </div>
    </div>

    <div class="hero-decoration">
      <v-icon class="decoration-icon">mdi-food</v-icon>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .welcome-hero {
    @apply tw-bg-gradient-to-r tw-from-orange-500 tw-to-amber-600 tw-z-0;
    @apply tw-text-white tw-p-8 tw-rounded-3xl;
    @apply tw-shadow-sm tw-relative tw-overflow-hidden;
    @apply tw-mb-6;

    .hero-content {
      @apply tw-relative tw-z-10;
      @apply tw-flex tw-flex-col tw-gap-0 md:tw-gap-6;
      @apply md:tw-flex-row md:tw-justify-between md:tw-items-center;

      .greeting-section {
        @apply tw-flex-1;

        .greeting {
          @apply tw-text-3xl tw-font-light tw-mb-2;
          @apply md:tw-text-4xl;
        }

        .subtitle {
          @apply tw-text-lg tw-opacity-90 tw-font-light;
        }
      }

      .hero-stats {
        @apply tw-flex tw-gap-6;

        .stat-item {
          @apply tw-flex tw-items-center tw-gap-3;
          @apply tw-bg-white/20 tw-backdrop-blur-sm;
          @apply tw-rounded-xl tw-px-4 tw-py-3;

          .stat-icon {
            @apply tw-text-2xl;
          }

          .stat-content {
            @apply tw-flex tw-flex-row tw-items-center tw-gap-2;
            @apply sm:tw-flex-col sm:tw-items-start sm:tw-gap-0;

            .stat-value {
              @apply tw-text-2xl tw-font-semibold;
            }

            .stat-label {
              @apply tw-text-sm tw-opacity-90;
            }
          }
        }
      }
    }

    .hero-decoration {
      @apply tw-absolute tw-right-0 tw-bottom-0;
      @apply tw-opacity-10;

      .decoration-icon {
        @apply tw-text-[200px];
        @apply tw-transform tw-translate-x-16 tw-translate-y-16;
      }
    }
  }

  // Mobile adjustments
  @media (max-width: 640px) {
    .welcome-hero {
      @apply tw-p-6;

      .hero-content {
        .greeting-section {
          .greeting {
            @apply tw-text-2xl;
          }

          .subtitle {
            @apply tw-text-base;
          }
        }

        .hero-stats {
          @apply tw-gap-4;

          .stat-item {
            @apply tw-px-3 tw-py-2;

            .stat-content {
              .stat-value {
                @apply tw-text-xl;
              }
            }
          }
        }
      }

      .hero-decoration {
        .decoration-icon {
          @apply tw-text-[150px];
        }
      }
    }
  }
</style>
